import React from "react";
import { connect } from 'react-redux';
import clsx from "clsx";
import _ from "lodash";

import Grid from "@material-ui/core/Grid";
import {Accordion, AccordionDetails, AccordionSummary} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import withStyles from "@material-ui/core/styles/withStyles";

import {withEuroOrDash} from "../../../../../utils/utils";
import ConfirmDeleteModal from "../../../../../components/BaseDialog/components/ConfirmDeleteModal";
import {BcaDeleteIconButton, BcaEditIconButton} from "../../../../../components/Buttons/BcaIconButton/BcaIconButton";
import {PortfolioManagerResource} from "../../../../../utils/api";
import {displaySuccessSnackBar} from "../../../../../components/SnackbarProvider/actions";
import EditAssetModal from "../../../EditAssetModal";
import {CLEARING_ACCOUNT_ASSET_TYPE} from "../../../../OtherAssets/components/AssetsList/components/AssetsCategories/constants";
import AssetForm from "../../../AssetForm";
import {DEPOT_ASSET_TYPE, ASSETS_LIABILITIES_ACTIONS, ASSETS_LIABILITIES_CONFIG} from "../../../constants";
import {useTabsSharedDataContext} from "../../../../CustomerDashboardV2/components_v2/TabsSharedData/TabsSharedData";
import {getDetailsUrl, getErrorMsg, isExternalContract} from "../../../utils";
import VirtualDepotDeleteButton
  from "../../../../CustomerDashboardV2/components_v2/ActionButtons/VirtualdepotActions/VirtualDepotDeleteButton";
import AssetCardLinks from "./components/AssetCardLinks";
import CustomTooltip from "../../../../../components/Tooltip";
import Link from "../../../../../components/Link";
import {scrollToFirstElementOfClass} from "../../../../RiskProfiling/utils";
import AssetBaseInfo from "./components/AssetBaseInfo";
import ContractDetailsLoader from "../../../AssetForm/components/ContractDetailsLoader";
import useContractDetails from "../../../hooks/useContractDetails";
import {getFormScheme} from "../../../AssetForm/forms_schemes";
import DynamicForm from "../../../AssetForm/components/DynamicForm";
import {getAuthSelector} from "../../../../../utils/redaxSelectors";

import styles from './styles';

const DeleteAssetBtn = connect()((props) => {
  const {dispatch, customerId, asset, groupId, assetTypeLabel, assetTypeConfig, onUpdate, disableTooltip, isDisabled} = props;

  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [assetDeleteRequestLoading, setAssetDeleteRequestLoading] = React.useState(false);

  const sharedData = useTabsSharedDataContext();

  const onCloseDeleteAsset = () => {
    setShowDeleteConfirm(false);
  };

  const onClickConfirmDeleteAsset = async () => {
    setAssetDeleteRequestLoading(true);
    try {
      const resource = _.get(sharedData, 'resource', PortfolioManagerResource);
      await resource.at(getDetailsUrl(customerId, asset)).delete();
      setAssetDeleteRequestLoading(false);
      dispatch(displaySuccessSnackBar(`${assetTypeLabel} gelöscht.`));
      onCloseDeleteAsset();
      onUpdate(ASSETS_LIABILITIES_ACTIONS.DELETE, groupId, asset);
    } catch (errors) {
      getErrorMsg(dispatch, errors);
      setAssetDeleteRequestLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  return (
    <>
      <BcaDeleteIconButton
        onClick={handleDeleteClick}
        color={'secondary'}
        disabled={isDisabled}
        tooltip={disableTooltip}
      />
      <ConfirmDeleteModal
        open={showDeleteConfirm}
        title={`${assetTypeLabel} löschen`}
        modalLoading={assetDeleteRequestLoading}
        handleClose={onCloseDeleteAsset}
        deleteTitle={'Ja, löschen'}
        onDeleteClick={onClickConfirmDeleteAsset}
      >
        {assetTypeConfig.confirmDeleteText}
      </ConfirmDeleteModal>
    </>
  )
});

const EditAssetBtn = (props) => {
  const {
    customerId, asset, groupId, onAssetUpdate, itemsForLinks, objectType,
    assetTypeLabel, isDisabled, disableTooltip
  } = props;

  // double-state: mount and close to keep closing animation
  const [showEditForm, setShowEditForm] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const handleEditClick = () => {
    setShowEditForm(true);
    setOpen(true);
  };

  const handleEditModalClose = () => {
    setOpen(false);
  };

  const handleAssetEdit = (asset, closeModal=true) => {
    if(closeModal) handleEditModalClose();
    onAssetUpdate(asset);
  };

  return (
    <>
      <BcaEditIconButton tooltip={disableTooltip} onClick={handleEditClick} disabled={isDisabled} />
      {showEditForm && (
        <EditAssetModal
          open={open}
          onExited={() => setShowEditForm(false)}
          asset={asset}
          objectType={objectType}
          groupId={groupId}
          onModalClose={handleEditModalClose}
          onAssetEdit={handleAssetEdit}
          customerId={customerId}
          assetTypeLabel={assetTypeLabel}
          itemsForLinks={itemsForLinks}
        />
      )}
    </>
  )
};

const AssetCard = (props) => {
  const {
    classes,
    objectType,
    customerId,
    asset,
    groupId,
    categoryIcon,
    onUpdate,
    onDepotsUpdate,
    itemsForLinks,
    auth: {user}
  } = props;

  const [expanded, setExpanded] = React.useState(false);

  const onAssetUpdate = React.useCallback((data) =>
    onUpdate(ASSETS_LIABILITIES_ACTIONS.UPDATE, groupId, data),
    [groupId, onUpdate]
  );
  const fetchState = useContractDetails(customerId, asset, onAssetUpdate, expanded);

  const assetTypeConfig = ASSETS_LIABILITIES_CONFIG[objectType];
  const assetTypeLabel = assetTypeConfig.singleLabel;
  const assetTypeId = asset.type_id || groupId;

  const linksContainerId = `${objectType}-${asset.id}`;
  const linksSectionAnchorClassName = `${linksContainerId}-links-anchor`;
  const onLinkCounterClick = () => {
    setExpanded(true);

    scrollToFirstElementOfClass(linksContainerId, [linksSectionAnchorClassName])
  };

  const isCash = assetTypeId === CLEARING_ACCOUNT_ASSET_TYPE.id;
  const isDepot = assetTypeId === DEPOT_ASSET_TYPE.id;
  let disableTooltip;
  let isExpandable = true;
  if(_.get(user, 'is_customer') && isExternalContract(assetTypeId) && asset.dataSource !== 'customer'){
    disableTooltip = 'Diese Kategorie kann derzeit nicht bearbeitet oder gelöscht werden.'
  } else if (asset.imported){
    disableTooltip = `${asset.name} wurde von der Gesellschaft bereitgestellt und kann nicht bearbeitet werden.`;
  } else if(isCash) {
    isExpandable = false;
    disableTooltip = 'Die Verrechnungskontodaten können nicht geändert oder gelöscht werden.'
  } else if(isDepot) {
    isExpandable = false;
    disableTooltip = asset.is_manual
      ? 'Die Bearbeitung des manuell hinzugefügten Depots kann direkt in der Vermögensübersicht vorgenommen werden.'
      : 'Sie können die Depotinformationen nicht ändern oder löschen.'
  }
  const isDisabled = !!disableTooltip;
  const dynamicFormScheme = isExpandable && getFormScheme(groupId, asset.sub_category, asset.type_id);

  const currentLinks = asset.linkedItems || [];
  const hasLinks = currentLinks.length > 0;

  return (
    <Grid item xs={12} data-id={`asset-${assetTypeId}-${asset.id}-card`}>
      <Accordion
        expanded={expanded}
        onChange={undefined}
        TransitionProps={{ unmountOnExit: true }}  // as we have list of assets on the page mount only when it's open
        classes={{root: classes.accordionRoot, rounded: classes.accordionRounded}}
      >
        <AccordionSummary
          expandIcon={isExpandable ? <ExpandMoreIcon /> : undefined}
          IconButtonProps={{
            classes: {edgeEnd: classes.accButtonIconEdgeEnd},
            onClick: () => setExpanded(!expanded)
          }}
          aria-controls="panel1d-content"
          id="panel1d-header"
          classes={{
            root: clsx(classes.accordionSummaryRoot, !isExpandable && 'no-expand'),
            expanded: classes.accordionSummaryExpanded,
            content: classes.accordionSummaryContent,
          }}
        >
          <Grid container spacing={2} alignItems={"center"}>
            <AssetBaseInfo
              icon={categoryIcon}
              subCategoryName={asset.sub_category_display}
              assetName={asset.name}
              expiredAt={asset.is_expired && asset.expiration_date}
              isFuture={asset.is_future}
            />
            <Grid item xs={'auto'} style={{textAlign: "right"}}>
              <b data-id={'asset-value'}>{withEuroOrDash(asset.asset_value)}</b>
              {hasLinks && (
                <>
                  <br/>
                  <CustomTooltip title={`Gesamtwert: ${withEuroOrDash(_.sumBy(currentLinks, 'asset_value'))}`}>
                    <span>
                      <Link
                        title={<>{currentLinks.length} {currentLinks.length === 1 ? assetTypeConfig.linkItemLabel : assetTypeConfig.linkItemsLabel}</>}
                        customClasses={{link: classes.linkCounter}}
                        onClick={onLinkCounterClick}
                      />
                    </span>
                  </CustomTooltip>
                </>
              )}
            </Grid>
            <Grid item xs={'auto'} style={{marginLeft: 'auto'}}>
              <Grid container spacing={1}>
                <Grid item xs>
                  <EditAssetBtn
                    customerId={customerId}
                    asset={asset}
                    objectType={objectType}
                    groupId={groupId}
                    assetTypeLabel={assetTypeLabel}
                    itemsForLinks={itemsForLinks}
                    onAssetUpdate={onAssetUpdate}
                    disableTooltip={disableTooltip}
                    isDisabled={isDisabled || fetchState.loading}
                  />
                </Grid>
                <Grid item xs>
                  {(isDepot && asset.is_manual) ?
                    <VirtualDepotDeleteButton
                      customerId={customerId}
                      portfolioId={asset.id}
                      portfolioName={asset.sub_category_display}
                      color={'secondary'}
                      onDelete={() => onDepotsUpdate(groupId, asset)}
                    /> :
                    <DeleteAssetBtn
                      customerId={customerId}
                      asset={asset}
                      groupId={groupId}
                      assetTypeLabel={assetTypeLabel}
                      assetTypeConfig={assetTypeConfig}
                      onUpdate={onUpdate}
                      disableTooltip={disableTooltip}
                      isDisabled={isDisabled || fetchState.loading}
                    />
                  }
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          {isExpandable && (
            <Grid container spacing={2} id={linksContainerId}>
              <Grid item xs={12} className={classes.subCategoryName}>Details</Grid>
              <ContractDetailsLoader fetchState={fetchState}>
                {!!dynamicFormScheme ? (
                  <DynamicForm
                    key={asset.updated}  // re-mount form when asset changed
                    schema={dynamicFormScheme}
                    defaults={asset}
                    viewOnly
                  />
                ) : (
                  <AssetForm
                    categoryId={assetTypeId}
                    defaults={asset}
                    viewOnly
                  />
                )}
                {hasLinks && (
                  <Grid item xs={12}>
                    <div className={linksSectionAnchorClassName}></div>
                    <AssetCardLinks currentLinks={currentLinks} objectType={objectType} mainItemTypeId={asset.type_id} />
                  </Grid>
                )}
              </ContractDetailsLoader>
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    </Grid>
  )
};

const mapStateToProps = (state) => ({
  auth: getAuthSelector(state),
});

export default connect(mapStateToProps)(withStyles(styles)(AssetCard));